import { Tooltip } from '@mui/material';
import React from 'react';
import './CustomSeatPicker.css';

const CustomSeatPicker = ({
    layout,
    selectedSeats,
    onSelect,
    onDeselect,
    maxSeats,
    isReadOnly = false,
    onSeatClick = null,
    allowLockedSeatClick = false,
    renderRowHeader = null,
    renderSeatCaption = null,
}) => {

    const handleSeatClick = (seat) => {
        if (isReadOnly) {
            if (allowLockedSeatClick && seat.status === 'locked' && onSeatClick) onSeatClick(seat);
            return;
        }
        if (seat.isReserved) return;

        if (onSeatClick) {
            onSeatClick(seat);
            return;
        }

        const isSelected = selectedSeats?.some(s => s.id === seat.id);
        if (isSelected) {
            onDeselect(seat);
        } else {
            if (selectedSeats.length < maxSeats) {
                onSelect(seat);
            }
        }
    };

    if (!layout || layout.length === 0) {
        return <div style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>No seat layout available for this section.</div>;
    }

    return (
        <div className="theater-layout-container">
            {/* The Stage */}
            <div className="theater-stage">
                <span>STAGE</span>
            </div>

            {/* Seating Area */}
            <div className="theater-seating-area">
                {layout.map((row, rowIndex) => {
                    const rowLabel = row.find(s => s)?.row || '';
                    
                    // Simple logic to add a slight curve effect based on row index
                    // (Optional, but adds to the theater feel)
                    // The lower the row index, the closer to the stage
                    // Let's keep it straight but styled beautifully.

                    const rowContent = (
                        <div className="theater-row">
                            {/* Row Label (Left) */}
                            <div className="theater-row-label">
                                {rowLabel}
                            </div>

                            {/* Seats */}
                            <div className="theater-seats-container">
                                {row.map((seat, seatIndex) => {
                                    if (!seat) return <div key={seatIndex} className="theater-aisle-spacer" />;

                                    const isSelected = selectedSeats?.some(s => s.id === seat.id);
                                    
                                    // Colors based on tier
                                    const baseColor = seat.color || '#ffffff';
                                    const bgColor = isSelected ? '#111' : (seat.statusColor || (seat.isReserved ? '#2a2a2a' : baseColor));
                                    const borderColor = isSelected ? baseColor : (seat.statusColor ? seat.statusColor : (seat.isReserved ? '#333' : 'rgba(0,0,0,0.15)'));
                                    const textColor = isSelected ? baseColor : (seat.statusColor ? '#fff' : (seat.isReserved ? '#555' : '#111'));
                                    
                                    const canClickLockedSeat = Boolean(
                                        isReadOnly && allowLockedSeatClick && seat.status === 'locked' && onSeatClick
                                    );

                                    const seatButton = (
                                        <button
                                            className={`theater-seat ${isSelected ? 'selected' : ''} ${seat.isReserved && !seat.statusColor ? 'reserved' : ''} ${seat.isHighlighted ? 'search-match' : ''}`}
                                            style={{
                                                backgroundColor: bgColor,
                                                borderColor: borderColor,
                                                color: textColor,
                                                cursor: canClickLockedSeat ? 'pointer' : (seat.isReserved || isReadOnly ? 'not-allowed' : 'pointer'),
                                                borderWidth: '2px',
                                                borderStyle: 'solid',
                                                boxShadow: isSelected ? `0 0 14px ${baseColor}90, inset 0 0 6px ${baseColor}30` : undefined
                                            }}
                                            onClick={() => handleSeatClick(seat)}
                                            disabled={canClickLockedSeat ? false : (seat.isReserved || isReadOnly)}
                                            aria-label={seat.buyerName ? `Seat ${seat.seatLabel || seat.number}, buyer ${seat.buyerName}` : `Seat ${seat.number}`}
                                        >
                                            <span className="theater-seat-number">{seat.number}</span>
                                        </button>
                                    );

                                    return (
                                        <Tooltip 
                                            key={seat.id} 
                                            title={seat.tooltip || seat.id} 
                                            arrow 
                                            placement="top"
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: '#1a1a1a',
                                                        color: '#fff',
                                                        border: '1px solid #333',
                                                        fontFamily: 'Inter',
                                                        fontWeight: 500,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                                    }
                                                },
                                                arrow: {
                                                    sx: {
                                                        color: '#1a1a1a',
                                                        '&::before': {
                                                            border: '1px solid #333',
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            {renderSeatCaption ? (
                                                <span className={`theater-seat-cell ${seat.isDimmed ? 'search-dimmed' : ''}`}>
                                                    {seatButton}
                                                    {renderSeatCaption(seat)}
                                                </span>
                                            ) : seatButton}
                                        </Tooltip>
                                    );
                                })}
                            </div>
                            
                            {/* Row Label (Right) */}
                            <div className="theater-row-label right-label">
                                {rowLabel}
                            </div>
                        </div>
                    );
                    return (
                        <React.Fragment key={rowIndex}>
                            {renderRowHeader?.(row, layout[rowIndex - 1])}
                            {rowContent}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default CustomSeatPicker;
